import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Activity, AlertTriangle, CheckCircle, Monitor, ChevronRight,
  BarChart2, HardDrive, Thermometer, Cpu, MemoryStick, Zap, BatteryWarning, Search, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL || (window.location.origin + '/api');

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

const GRADE_CFG = {
  healthy:   { label: 'Saludable',         bg: 'var(--green-dim)', color: 'var(--green)', border: '#86efac' },
  monitor:   { label: 'Monitorear',         bg: 'var(--amber-dim)', color: 'var(--amber)', border: '#fde047' },
  attention: { label: 'Requiere atención',  bg: 'var(--amber-dim)', color: 'var(--amber)', border: '#fdba74' },
  critical:  { label: 'Crítico',            bg: 'var(--red-dim)', color: 'var(--red)', border: 'var(--red-dim)' },
};

const SEVERITY_CFG = {
  critical: { bg: 'var(--red-dim)', color: 'var(--red)', border: 'var(--red-dim)', label: 'Crítico'    },
  warning:  { bg: 'var(--amber-dim)', color: 'var(--amber)', border: 'var(--amber-dim)', label: 'Advertencia' },
  info:     { bg: 'var(--accent-dim)', color: 'var(--accent)', border: 'var(--accent-dim)', label: 'Informativo' },
};

const ALERT_TYPE_LABEL = {
  temperature_cpu:    'Temperatura CPU',
  cpu_throttling:     'CPU Throttling',
  ram_usage:          'Uso de RAM',
  ram_hardware_error: 'Error Hardware RAM',
  battery_wear:       'Desgaste Batería',
  smart:              'S.M.A.R.T.',
};
const ALERT_TYPE_ICON = {
  temperature_cpu:    Thermometer,
  cpu_throttling:     Zap,
  ram_usage:          MemoryStick,
  ram_hardware_error: AlertTriangle,
  battery_wear:       BatteryWarning,
  smart:              HardDrive,
};

// Filtros por patrón de salud (mapean a los alert_type del backend, ver /top-at-risk).
const PROBLEM_FILTERS = [
  { key: 'temperature', label: 'Temp. CPU',  Icon: Thermometer },
  { key: 'throttling',  label: 'Throttling', Icon: Zap },
  { key: 'ram',         label: 'RAM alta',   Icon: MemoryStick },
  { key: 'ram_error',   label: 'Error RAM',  Icon: AlertTriangle },
  { key: 'battery',     label: 'Batería',    Icon: BatteryWarning },
  { key: 'smart',       label: 'Disco',      Icon: HardDrive },
];
const GRADE_FILTERS = [
  { key: 'critical',   label: 'Crítico' },
  { key: 'attention',  label: 'Requiere atención' },
  { key: 'monitor',    label: 'Monitorear' },
  { key: 'healthy',    label: 'Saludable' },
];

export default function HardwareHealth() {
  const [topAtRisk, setTopAtRisk] = useState([]);
  const [alerts, setAlerts]       = useState([]);
  const [loading, setLoading]     = useState(true);
  const [riskLoading, setRiskLoading] = useState(false);
  const [error, setError]         = useState(null);

  // Filtros de la lista de equipos en riesgo
  const [search, setSearch]   = useState('');
  const [problem, setProblem] = useState('');   // '' = todos
  const [grade, setGrade]     = useState('');    // '' = todos

  const token   = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  // Carga inicial: alertas (fijas) + primera tanda de equipos en riesgo.
  useEffect(() => {
    fetch(`${API_URL}/hardware/alerts`, { headers })
      .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
      .then(al => setAlerts(Array.isArray(al) ? al : []))
      .catch(err => {
        console.error('[HardwareHealth]', err);
        setError('Error cargando datos. Intentá cerrar sesión y volver a ingresar.');
      })
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  // Equipos en riesgo: re-fetch al cambiar filtros. Texto con debounce de 300ms.
  useEffect(() => {
    const t = setTimeout(() => {
      const params = new URLSearchParams({ limit: '20' });
      if (search.trim()) params.set('q', search.trim());
      if (problem)       params.set('problem', problem);
      if (grade)         params.set('grade', grade);
      setRiskLoading(true);
      fetch(`${API_URL}/hardware/top-at-risk?${params}`, { headers })
        .then(r => { if (!r.ok) throw new Error(r.status); return r.json(); })
        .then(risk => setTopAtRisk(Array.isArray(risk) ? risk : []))
        .catch(err => console.error('[HardwareHealth] top-at-risk', err))
        .finally(() => setRiskLoading(false));
    }, 300);
    return () => clearTimeout(t);
  }, [search, problem, grade]); // eslint-disable-line

  const criticals = alerts.filter(a => a.severity === 'critical').length;
  const warnings  = alerts.filter(a => a.severity === 'warning').length;
  const hasFilters = !!(search.trim() || problem || grade);
  const clearFilters = () => { setSearch(''); setProblem(''); setGrade(''); };

  if (loading) return (
    <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
      <div className="spinner" />
    </div>
  );

  if (error) return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px' }}>
      <div style={{ background: 'var(--red-dim)', border: '1px solid var(--red-dim)', borderRadius: 8, padding: '16px 20px', color: 'var(--red)', fontSize: 14 }}>
        ⚠️ {error}
      </div>
    </div>
  );

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: '32px 24px' }}>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
          <Activity size={22} style={{ opacity: 0.7 }} />
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700 }}>Salud del Hardware</h1>
        </div>
        <p style={{ margin: 0, color: '#666', fontSize: 14 }}>
          Monitoreo predictivo de hardware físico — S.M.A.R.T., temperatura, RAM y batería.
        </p>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(180px,1fr))', gap: 16, marginBottom: 28 }}>
        <StatCard label="Alertas críticas"  value={criticals} color="var(--red)" bg="var(--red-dim)" Icon={AlertTriangle} />
        <StatCard label="Advertencias"      value={warnings}  color="var(--amber)" bg="var(--amber-dim)" Icon={AlertTriangle} />
        <StatCard label="Equipos en riesgo" value={topAtRisk.filter(a => a.hw_health_grade !== 'healthy').length} color="var(--ink-2)" bg="var(--bg-subtle)" Icon={Monitor} />
        <StatCard label="Total monitoreados" value={topAtRisk.length} color="#0369a1" bg="#e0f2fe" Icon={BarChart2} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 }}>

        {/* Top at-risk */}
        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--bg)' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
              <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
                ⚠️ Equipos más críticos {hasFilters && <span style={{ fontWeight: 500, color: 'var(--ink-3)' }}>({topAtRisk.length})</span>}
              </h2>
              {hasFilters && (
                <button onClick={clearFilters}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', fontSize: 12, fontWeight: 600, padding: 0 }}>
                  <X size={13} /> Limpiar
                </button>
              )}
            </div>
            <p style={{ margin: '4px 0 12px', fontSize: 12, color: 'var(--ink-3)' }}>
              Top 20 ordenados por score de salud más bajo
            </p>

            {/* Búsqueda por texto */}
            <div style={{ position: 'relative', marginBottom: 10 }}>
              <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-3)' }} />
              <input
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Buscar por nombre, modelo o fabricante…"
                style={{ width: '100%', boxSizing: 'border-box', padding: '8px 10px 8px 30px', fontSize: 13, borderRadius: 8, border: '1px solid var(--bg)', background: 'var(--bg-subtle)', color: 'var(--ink)', outline: 'none' }}
              />
            </div>

            {/* Filtros por patrón de salud */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
              {PROBLEM_FILTERS.map(({ key, label, Icon }) => {
                const active = problem === key;
                return (
                  <button key={key} onClick={() => setProblem(active ? '' : key)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 9px', fontSize: 11, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
                      border: `1px solid ${active ? 'var(--accent)' : 'var(--bg)'}`,
                      background: active ? 'var(--accent-dim)' : 'var(--bg-subtle)',
                      color: active ? 'var(--accent)' : 'var(--ink-2)' }}>
                    <Icon size={12} /> {label}
                  </button>
                );
              })}
            </div>
            {/* Filtros por grado */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {GRADE_FILTERS.map(({ key, label }) => {
                const active = grade === key;
                const g = GRADE_CFG[key];
                return (
                  <button key={key} onClick={() => setGrade(active ? '' : key)}
                    style={{ padding: '4px 9px', fontSize: 11, fontWeight: 600, borderRadius: 6, cursor: 'pointer',
                      border: `1px solid ${active ? g.color : 'var(--bg)'}`,
                      background: active ? g.bg : 'var(--bg-subtle)',
                      color: active ? g.color : 'var(--ink-2)' }}>
                    {label}
                  </button>
                );
              })}
            </div>
          </div>
          {riskLoading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
              <div className="spinner" />
            </div>
          ) : topAtRisk.length === 0 ? (
            <div style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
              <CheckCircle size={32} style={{ opacity: 0.3, display: 'block', margin: '0 auto 8px' }} />
              {hasFilters ? 'Ningún equipo coincide con los filtros' : 'Sin equipos con datos de salud'}
            </div>
          ) : (
            <div>
              {topAtRisk.map(asset => {
                const cfg = GRADE_CFG[asset.hw_health_grade] || GRADE_CFG.monitor;
                return (
                  <Link key={asset.id} to={`/assets/${asset.id}`}
                    style={{ display: 'flex', alignItems: 'center', padding: '12px 20px', borderBottom: '1px solid var(--bg-subtle)', textDecoration: 'none', color: 'inherit' }}
                    onMouseEnter={e => e.currentTarget.style.background='var(--bg-subtle)'}
                    onMouseLeave={e => e.currentTarget.style.background='transparent'}>
                    {/* Score circle */}
                    <div style={{ width: 44, height: 44, borderRadius: '50%', background: cfg.bg, border: `2px solid ${cfg.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginRight: 14 }}>
                      <span style={{ fontSize: 13, fontWeight: 700, color: cfg.color }}>{asset.hw_health_score ?? '?'}</span>
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--ink)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {asset.hostname}
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                        {asset.model || asset.manufacturer || '—'}
                      </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                      {asset.last_boot_time && (() => {
                        const days = Math.floor((Date.now() - new Date(asset.last_boot_time).getTime()) / 86400000);
                        if (days < 7) return null;
                        return (
                          <span title="Días sin reiniciar" style={{ color: days >= 14 ? 'var(--red)' : 'var(--amber)', fontSize: 11, fontWeight: 700 }}>
                            {days}d uptime
                          </span>
                        );
                      })()}
                      {parseInt(asset.critical_count) > 0 && (
                        <span style={{ color: 'var(--red)', fontSize: 11, fontWeight: 700 }}>
                          {asset.critical_count} crítico{asset.critical_count > 1 ? 's' : ''}
                        </span>
                      )}
                      {parseInt(asset.warning_count) > 0 && (
                        <span style={{ color: 'var(--amber)', fontSize: 11, fontWeight: 700 }}>
                          {asset.warning_count} advert.
                        </span>
                      )}
                      <ChevronRight size={14} style={{ color: 'var(--ink-3)' }} />
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Alertas activas */}
        <div className="card" style={{ padding: 0 }}>
          <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--bg)' }}>
            <h2 style={{ margin: 0, fontSize: 15, fontWeight: 700 }}>
              🚨 Alertas activas
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--ink-3)' }}>
              {alerts.length} alerta{alerts.length !== 1 ? 's' : ''} activa{alerts.length !== 1 ? 's' : ''}
            </p>
          </div>
          <div style={{ maxHeight: 440, overflowY: 'auto' }}>
            {alerts.length === 0 ? (
              <div style={{ padding: 32, textAlign: 'center', color: 'var(--ink-3)', fontSize: 13 }}>
                <CheckCircle size={32} style={{ opacity: 0.3, display: 'block', margin: '0 auto 8px' }} />
                Sin alertas activas
              </div>
            ) : alerts.map(al => {
              const sev = SEVERITY_CFG[al.severity] || SEVERITY_CFG.info;
              return (
                <div key={al.id} style={{ padding: '12px 20px', borderBottom: '1px solid var(--bg-subtle)' }}>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: sev.color, flexShrink: 0, marginTop: 1 }}>
                      {sev.label}
                    </span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, color: 'var(--ink-2)', marginBottom: 2 }}>
                        <span style={{ fontWeight: 600, color: 'var(--ink-2)', display: 'inline-flex', alignItems: 'center', gap: 5, verticalAlign: 'middle' }}>
                          {(() => { const Ic = ALERT_TYPE_ICON[al.alert_type]; return Ic ? <Ic size={14} style={{ color: sev.color }} /> : null; })()}
                          {ALERT_TYPE_LABEL[al.alert_type] || al.alert_type}
                        </span>
                        {' · '}
                        <Link to={`/assets/${al.asset_id}`} style={{ color: 'var(--accent)', fontWeight: 600, textDecoration: 'none' }}>
                          {al.hostname}
                        </Link>
                      </div>
                      <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>{al.message}</div>
                      <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2 }}>
                        Valor: <strong>{al.raw_value}</strong> · Umbral: {al.threshold} · {fmtDate(al.last_seen)}
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

      </div>
    </div>
  );
}

function StatCard({ label, value, color, bg, Icon }) {
  return (
    <div className="card" style={{ padding: '18px 20px', display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{ width: 44, height: 44, borderRadius: 12, background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        {Icon && <Icon size={22} style={{ color }} />}
      </div>
      <div>
        <div style={{ fontSize: 24, fontWeight: 800, color }}>{value}</div>
        <div style={{ fontSize: 12, color: 'var(--ink-2)' }}>{label}</div>
      </div>
    </div>
  );
}
